/**
 * CurrencyContext - Global Currency Management for VivyaWatch
 * 
 * Provides:
 * - Automatic country detection based on IP
 * - Real-time currency conversion from USD
 * - Global currency state for all pricing components
 * - Manual currency override option
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  detectCountry,
  fetchExchangeRates,
  convertPrice,
  formatPrice,
  getAnnualPrice,
  BASE_PRICES_USD,
  CURRENCY_CONFIG,
} from '../services/currencyService';

// Create context
const CurrencyContext = createContext(undefined);

// Initial fallback rates to prevent undefined errors during loading
// These are approximate rates - live rates are fetched from API
// Current rate: 1 USD = 91 INR (as of Jan 31, 2026)
const INITIAL_RATES = {
  // Major currencies
  USD: 1, EUR: 0.92, GBP: 0.79, JPY: 155, CNY: 7.25,
  // Americas
  CAD: 1.44, MXN: 20.50, BRL: 5.85, ARS: 1050, CLP: 980, COP: 4200, PEN: 3.75,
  UYU: 44, BOB: 6.91, PYG: 7800, VES: 40, CRC: 510, GTQ: 7.75, HNL: 25,
  NIO: 37, PAB: 1, DOP: 60, JMD: 156, TTD: 6.80, BBD: 2, BSD: 1, BZD: 2,
  GYD: 210, SRD: 32, AWG: 1.79, ANG: 1.79, XCD: 2.70,
  // Europe
  CHF: 0.91, SEK: 10.95, NOK: 11.20, DKK: 7.05, PLN: 4.05, CZK: 23.50,
  HUF: 380, RON: 4.70, BGN: 1.85, HRK: 7.10, RSD: 110, ISK: 140,
  RUB: 92, UAH: 42, GEL: 2.75, AMD: 400, AZN: 1.70, BYN: 3.30,
  MDL: 18, MKD: 58, ALL: 95, BAM: 1.85,
  // Asia Pacific
  AUD: 1.57, NZD: 1.77, INR: 91, SGD: 1.35, HKD: 7.80, TWD: 32, KRW: 1350,
  PHP: 57, THB: 34, MYR: 4.45, IDR: 16200, VND: 25400, PKR: 280, BDT: 122,
  LKR: 320, NPR: 135, MMK: 2100, KHR: 4100, LAK: 21000, BND: 1.35, MNT: 3450,
  KZT: 510, UZS: 12800, KGS: 90, TJS: 11, AFN: 70, FJD: 2.30, PGK: 3.95,
  WST: 2.80, SBD: 8.50, TOP: 2.40, VUV: 120,
  // Middle East
  AED: 3.67, SAR: 3.75, QAR: 3.64, KWD: 0.31, BHD: 0.38, OMR: 0.38, JOD: 0.71,
  ILS: 3.65, TRY: 32, LBP: 89500, EGP: 50, IQD: 1310, YER: 250, SYP: 13000,
  // Africa
  ZAR: 18.35, NGN: 1600, KES: 155, GHS: 16, UGX: 3750, TZS: 2650, RWF: 1350,
  ETB: 125, MAD: 10, DZD: 135, TND: 3.15, LYD: 4.90, XOF: 620, XAF: 620,
  MUR: 46, SCR: 14, BWP: 14, NAD: 18.35, SZL: 18.35, LSL: 18.35, MWK: 1750,
  ZMW: 28, MZN: 64, AOA: 830, CVE: 104, GMD: 72, GNF: 8600, SLL: 22500,
  LRD: 195, MGA: 4650, CDF: 2800, BIF: 2900, DJF: 178, ERN: 15, SOS: 570,
  SDG: 600, SSP: 1300, STN: 23, KMF: 465,
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [country, setCountry] = useState('US');
  const [rates, setRates] = useState(INITIAL_RATES); // Start with fallback rates
  const [loading, setLoading] = useState(false); // Start as false — no eager loading
  const [error, setError] = useState(null);
  const initRef = useRef(false); // Track if initialization has been triggered

  // Lazy initialization — only called when a consumer (useCurrency) mounts
  const requestInit = useCallback(() => {
    if (initRef.current) return; // Already initialized or in-progress
    initRef.current = true;

    const initialize = async () => {
      try {
        setLoading(true);

        // Run both in parallel
        const [countryData, exchangeRates] = await Promise.all([
          detectCountry(),
          fetchExchangeRates(),
        ]);

        setCountry(countryData.country);
        setCurrency(countryData.currency);
        setRates(exchangeRates);
        setError(null);
      } catch (err) {
        console.error('Currency initialization failed:', err);
        setError('Failed to detect currency');
        // Fallback to USD
        setCurrency('USD');
        setCountry('US');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  // Manual currency change
  const changeCurrency = useCallback((newCurrency) => {
    if (CURRENCY_CONFIG[newCurrency]) {
      setCurrency(newCurrency);
    }
  }, []);

  // Convert a USD price to current currency
  const convert = useCallback((usdAmount) => {
    // Safety check: ensure rates is defined
    if (!rates || Object.keys(rates).length === 0) {
      return usdAmount; // Return USD amount if rates not loaded
    }
    return convertPrice(usdAmount, rates, currency);
  }, [rates, currency]);

  // Format a price with current currency symbol
  const format = useCallback((amount) => {
    return formatPrice(amount, currency);
  }, [currency]);

  // Get formatted price for a tier
  const getTierPrice = useCallback((tierKey, billing = 'monthly') => {
    const basePrice = BASE_PRICES_USD[tierKey];
    if (basePrice === null) return 'Custom';
    
    const convertedMonthly = convert(basePrice);
    
    if (billing === 'annual' || billing === 'yearly') {
      const annual = getAnnualPrice(convertedMonthly);
      return format(annual);
    }
    
    return format(convertedMonthly);
  }, [convert, format]);

  // Get raw converted prices (not formatted)
  const getPrices = useCallback(() => {
    return {
      starter: {
        monthly: convert(BASE_PRICES_USD.starter),
        annual: getAnnualPrice(convert(BASE_PRICES_USD.starter)),
      },
      professional: {
        monthly: convert(BASE_PRICES_USD.professional),
        annual: getAnnualPrice(convert(BASE_PRICES_USD.professional)),
      },
      enterprise: {
        monthly: null,
        annual: null,
      },
    };
  }, [convert]);

  // Get currency symbol
  const getSymbol = useCallback(() => {
    return CURRENCY_CONFIG[currency]?.symbol || '$';
  }, [currency]);

  // Get all available currencies for selector
  // Null-guard: CURRENCY_CONFIG is a module-level constant but adding a guard
  // makes it safe against any future circular-dependency or lazy-load edge case.
  const availableCurrencies = CURRENCY_CONFIG ? Object.keys(CURRENCY_CONFIG) : [];

  const value = {
    currency,
    country,
    loading,
    error,
    rates,
    changeCurrency,
    convert,
    format,
    getTierPrice,
    getPrices,
    getSymbol,
    availableCurrencies,
    currencyConfig: CURRENCY_CONFIG[currency],
    _requestInit: requestInit, // Internal: used by useCurrency hook
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use currency context
// Triggers lazy initialization on first consumer mount
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }

  // Trigger initialization when a pricing component actually mounts
  useEffect(() => {
    context._requestInit();
  }, [context._requestInit]);

  // Return context without internal _requestInit
  const { _requestInit, ...publicContext } = context;
  return publicContext;
};

export default CurrencyContext;

